import {RestrictedFeatureEnum} from '../Models/RestrictedFeatureEnum';

const STANDARD_PAID_PLANS = [
  {
    name: 'Starter',
    label: 'A practical next step',
    description: 'Unlock more tools as your workspace begins to grow.',
    benefits: ['Feature access', 'More storage', 'Workflow tools'],
  },
  {
    name: 'Growth',
    label: 'For growing operations',
    description: 'Support larger workflows and teams.',
    benefits: ['Everything in Starter', 'Higher usage limits', 'More capacity'],
  },
  {
    name: 'Scale',
    label: 'For established businesses',
    description: 'Operate with the highest available limits.',
    benefits: ['Everything in Growth', 'Highest usage limits', 'Priority support'],
  },
];

export const FEATURE_UPGRADE_CATALOG = {
  bundle_operations: {
    minimumPlan: 'Starter',
    eyebrow: 'Sell products together',
    title: 'Create fixed and mix-and-match bundles.',
    description: 'Combine existing listings into curated bundles and let customers select eligible items at checkout.',
    plans: STANDARD_PAID_PLANS,
  },
  [RestrictedFeatureEnum.variantTypes]: {
    minimumPlan: 'Starter',
    eyebrow: 'Make one listing work harder',
    title: 'Let customers choose options from one listing.',
    description:
      'Offer sizes, colours, or other choices without duplicating listings or splitting your stock management.',
    beforeLabel: 'Without variants',
    beforeItems: ['T-shirt — Small', 'T-shirt — Medium', 'T-shirt — Large'],
    afterLabel: 'With variants',
    afterTitle: 'T-shirt',
    afterOptions: ['Small', 'Medium', 'Large'],
    plans: [
      {
        name: 'Starter',
        label: 'Best next step',
        description: 'For a growing catalogue that needs clearer product choices.',
        benefits: ['Listing variants', 'More storage', 'Import and export tools'],
      },
      {
        name: 'Growth',
        label: 'For growing operations',
        description: 'For teams managing a larger catalogue and more workflows.',
        benefits: ['Everything in Starter', 'Higher usage limits', 'More team capacity'],
      },
      {
        name: 'Scale',
        label: 'For established businesses',
        description: 'For high-volume teams that need room to operate at scale.',
        benefits: ['Everything in Growth', 'Highest usage limits', 'Priority support'],
      },
    ],
  },
  [RestrictedFeatureEnum.attributes]: {
    minimumPlan: 'Starter',
    eyebrow: 'Shape the information you collect',
    title: 'Capture the details that make your catalogue useful.',
    description: 'Create custom fields for the facts customers and your team need to see on every listing or account.',
    plans: [
      {name: 'Starter', label: 'Best next step', description: 'Add the structured information your workflow needs.', benefits: ['Custom attributes', 'Listing variants', 'Import and export tools']},
      {name: 'Growth', label: 'For growing operations', description: 'Keep more data and workflows organised as the catalogue grows.', benefits: ['Everything in Starter', 'Higher usage limits', 'More storage']},
      {name: 'Scale', label: 'For established businesses', description: 'Standardise richer data across a high-volume operation.', benefits: ['Everything in Growth', 'Highest usage limits', 'Priority support']},
    ],
  },
  [RestrictedFeatureEnum.bulkOperations]: {
    minimumPlan: 'Starter',
    eyebrow: 'Move faster with your catalogue',
    title: 'Import and export records without repetitive manual work.',
    description: 'Bring structured records in, make offline changes, and export them when your team needs a complete working copy.',
    plans: [
      {name: 'Starter', label: 'Best next step', description: 'For teams ready to work beyond one record at a time.', benefits: ['Import and export tools', 'Listing variants', 'More storage']},
      {name: 'Growth', label: 'For growing operations', description: 'Move larger catalogues and teams through routine work faster.', benefits: ['Everything in Starter', 'Higher usage limits', 'More team capacity']},
      {name: 'Scale', label: 'For established businesses', description: 'Support high-volume operational workflows at scale.', benefits: ['Everything in Growth', 'Highest usage limits', 'Priority support']},
    ],
  },
  [RestrictedFeatureEnum.languages]: {
    minimumPlan: 'Starter',
    eyebrow: 'Speak to more customers',
    title: 'Make your project available in more languages.',
    description: 'Manage the customer-facing language experience as you expand into new regions and audiences.',
    plans: STANDARD_PAID_PLANS,
  },
  [RestrictedFeatureEnum.currencies]: {
    minimumPlan: 'Starter',
    eyebrow: 'Sell across markets',
    title: 'Support customers who shop in different currencies.',
    description: 'Configure the currencies your business needs as you operate across regions.',
    plans: STANDARD_PAID_PLANS,
  },
  [RestrictedFeatureEnum.email]: {
    minimumPlan: 'Starter',
    eyebrow: 'Make every customer email yours',
    title: 'Send email from a brand your customers recognise.',
    description: 'Use your own sender identity and visual branding so marketplace communications stay consistent.',
    plans: [
      {name: 'Starter', label: 'Best next step', description: 'Start presenting a more consistent customer experience.', benefits: ['Email whitelabelling', 'Custom email templates', 'More storage']},
      {name: 'Growth', label: 'For growing operations', description: 'Run a stronger, more polished customer communication workflow.', benefits: ['Everything in Starter', 'Higher usage limits', 'More capacity']},
      {name: 'Scale', label: 'For established businesses', description: 'Support a complete enterprise-ready customer experience.', benefits: ['Everything in Growth', 'Priority support', 'Highest usage limits']},
    ],
  },
  [RestrictedFeatureEnum.customizeEmailTemplates]: {
    minimumPlan: 'Starter',
    eyebrow: 'Improve important customer messages',
    title: 'Make system emails match your workflow and tone.',
    description: 'Tailor the messages customers receive at key moments without changing your marketplace setup.',
    plans: STANDARD_PAID_PLANS,
  },
  [RestrictedFeatureEnum.api]: {
    minimumPlan: 'Starter',
    eyebrow: 'Connect your own tools',
    title: 'Use the API when your workflow needs to go further.',
    description: 'Connect your marketplace with internal tools and automation when manual work no longer scales.',
    plans: STANDARD_PAID_PLANS,
  },
  [RestrictedFeatureEnum.headlessApi]: {
    minimumPlan: 'Starter',
    eyebrow: 'Build beyond the standard interface',
    title: 'Use headless APIs for a fully custom experience.',
    description: 'Choose the flexibility to build your own customer-facing experiences on top of Tradly.',
    plans: STANDARD_PAID_PLANS,
  },
  [RestrictedFeatureEnum.auditLogs]: {
    minimumPlan: 'Growth',
    eyebrow: 'Keep a longer compliance history',
    title: 'Review more than the latest five audit events.',
    description:
      'Growth and Scale plans can review the complete audit history and export it for compliance workflows.',
    plans: [
      {
        name: 'Growth',
        label: 'For growing operations',
        description: 'Review the full audit history as your team and activity grow.',
        benefits: ['Full audit history', 'Audit log export', 'Higher usage limits'],
      },
      {
        name: 'Scale',
        label: 'For established businesses',
        description: 'Keep complete compliance visibility for high-volume operations.',
        benefits: ['Everything in Growth', 'Highest usage limits', 'Priority support'],
      },
    ],
  },
  [RestrictedFeatureEnum.rbac]: {
    minimumPlan: 'Growth',
    minimumRank: 2,
    eyebrow: 'Keep your workspace secure',
    title: 'Control who can see and change what.',
    description:
      'Roles let you give each teammate only the access they need. Available on Growth and above.',
    plans: [
      {
        name: 'Growth',
        label: 'For growing teams',
        description: 'Assign roles and permissions to every teammate.',
        benefits: ['Role-based access control', 'Per-module permissions', 'Higher usage limits'],
      },
      {
        name: 'Scale',
        label: 'For established businesses',
        description: 'Everything in Growth, with the highest limits.',
        benefits: ['Everything in Growth', 'Highest usage limits', 'Priority support'],
      },
    ],
  },
};

export const GENERIC_UPGRADE_CONTENT = {
  minimumPlan: 'a paid plan',
  eyebrow: 'Expand your workspace',
  title: 'Unlock this feature when you are ready.',
  description:
    'Your current work remains unchanged while you compare the plan that includes this capability.',
  plans: STANDARD_PAID_PLANS,
};
